/**
 * 长时间无动态ULD列表表格组件
 * 展示长时间没有流转记录的ULD板，帮助识别异常或遗失的ULD
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Table,
    Card,
    Typography,
    Space,
    Button,
    Tag,
    Tooltip,
    Modal,
    Spin,
    Alert,
    List,
    Select,
    Row,
    Col,
    message
} from 'antd';
import {
    ReloadOutlined,
    EyeOutlined,
    TagsOutlined,
    ExclamationCircleOutlined,
    InfoCircleOutlined,
    WarningOutlined,
    ClockCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { uldService } from '../../../../api/enhancedApi';
import { 
    ULD_STATUS_LABELS, 
    ULD_STATUS_COLORS,
    LOCATION_TYPE_LABELS,
    ULD_CORE_STATUS_LABELS,
    ULD_AVAILABILITY_LABELS,
    ULD_CORE_STATUS_COLORS,
    ULD_AVAILABILITY_COLORS
} from '../constants/statisticsConstants';
import { ULD_CORE_STATUS, ULD_AVAILABILITY } from '../../UldManagement/constants/uldConstantsV3';
import { applyInactiveTableQuery, classifyInactiveRisk, daysSinceLastMoveUtc } from '../../lib/inactiveUld';

const { Text, Title } = Typography;
const { Option } = Select;

const UldInactiveTable = ({ refreshTrigger, onFiltersChange }) => {
    const [allData, setAllData] = useState([]);
    const [backendTotal, setBackendTotal] = useState(0);
    const [tableFilters, setTableFilters] = useState({});
    const [tableSorter, setTableSorter] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [inactiveDays, setInactiveDays] = useState(7);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
    });

    const queriedData = useMemo(
        () => applyInactiveTableQuery(allData, { filters: tableFilters, sorter: tableSorter }),
        [allData, tableFilters, tableSorter],
    );

    const data = useMemo(() => {
        const startIndex = (pagination.current - 1) * pagination.pageSize;
        return queriedData.slice(startIndex, startIndex + pagination.pageSize);
    }, [queriedData, pagination.current, pagination.pageSize]);

    useEffect(() => {
        setPagination((prev) => {
            if (prev.total === queriedData.length) {
                return prev;
            }
            const maxPage = Math.max(1, Math.ceil(queriedData.length / prev.pageSize) || 1);
            return {
                ...prev,
                total: queriedData.length,
                current: Math.min(prev.current, maxPage),
            };
        });
    }, [queriedData.length]);
    
    const fetchInactiveUlds = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await uldService.getInactiveUlds({
                page: 1,
                page_size: 500,
                inactive_days: inactiveDays,
                sort_by: 'updated_at',
                sort_order: 'desc',
            });
            const { items, total } = response.data;
            setBackendTotal(total || 0);

            const processedData = (items || []).map((item) => {
                const days = daysSinceLastMoveUtc(item.stock?.last_moved_at);
                return {
                    ...item,
                    daysSinceLastMove: days,
                    riskLevel: classifyInactiveRisk(days),
                };
            });

            setAllData(processedData);
            setPagination((prev) => ({ ...prev, current: 1 }));
        } catch (err) {
            setError('获取数据失败，请稍后重试');
        } finally {
            setLoading(false);
        }
    }, [inactiveDays]);

    // 获取风险等级标签
    const getRiskTag = (riskLevel, days) => {
        const configs = {
            high: { color: 'red', text: '高风险', icon: <WarningOutlined /> },
            medium: { color: 'orange', text: '中风险', icon: <ExclamationCircleOutlined /> },
            low: { color: 'yellow', text: '低风险', icon: <ClockCircleOutlined /> },
            normal: { color: 'green', text: '正常', icon: null },
            unknown: { color: 'gray', text: '未知', icon: null },
        };
        
        const config = configs[riskLevel] || configs.unknown;
        
        return (
            <Tag color={config.color} icon={config.icon}>
                {config.text}
                {days !== null && ` (${days}天)`}
            </Tag>
        );
    };

    // 优化：只在关键依赖变化时触发数据获取
    useEffect(() => {
        fetchInactiveUlds();
    }, [fetchInactiveUlds, refreshTrigger]); // 添加fetchInactiveUlds依赖确保能正确响应变化

    // 在组件初始化时通知父组件当前的筛选条件
    useEffect(() => {
        if (onFiltersChange) {
            onFiltersChange({ inactive_days: inactiveDays });
        }
    }, []); // 只在组件挂载时执行一次

    // 表格列定义
    const columns = [
        {
            title: 'ULD识别码',
            dataIndex: ['master', 'uld_identifier'],
            key: 'uld_identifier',
            width: 120,
            fixed: 'left',
            render: (identifier) => (
                <Text strong style={{ color: '#1890ff' }}>
                    {identifier}
                </Text>
            ),
        },
        {
            title: 'ULD类型',
            dataIndex: ['master', 'uld_type'],
            key: 'uld_type',
            width: 80,
            render: (type) => (
                <Tag color="blue">{type}</Tag>
            ),
        },
        {
            title: '所有者',
            dataIndex: ['master', 'owner'],
            key: 'owner',
            width: 150,
            ellipsis: {
                showTitle: false,
            },
            render: (owner) => (
                <Tooltip title={owner}>
                    {owner || '未指定'}
                </Tooltip>
            ),
        },
        {
            title: '核心状态',
            dataIndex: ['stock', 'core_status'],
            key: 'core_status',
            width: 100,
            render: (coreStatus) => {
                const statusEnum = ULD_CORE_STATUS[coreStatus] || coreStatus;
                const statusName = ULD_CORE_STATUS_LABELS[statusEnum] || statusEnum;
                const color = ULD_CORE_STATUS_COLORS[statusEnum] || 'default';
                return <Tag color={color}>{statusName}</Tag>;
            },
        },
        {
            title: '可用性',
            dataIndex: ['stock', 'availability'],
            key: 'availability',
            width: 100,
            render: (availability) => {
                const availabilityEnum = ULD_AVAILABILITY[availability] || availability;
                const availabilityName = ULD_AVAILABILITY_LABELS[availabilityEnum] || availabilityEnum;
                const color = ULD_AVAILABILITY_COLORS[availabilityEnum] || 'default';
                return <Tag color={color}>{availabilityName}</Tag>;
            },
        },
        {
            title: '当前位置',
            key: 'current_location',
            width: 150,
            render: (_, record) => {
                const stock = record.stock;
                if (!stock) return '-';
                
                const locationType = stock.current_location_type;
                                 const locationLabel = LOCATION_TYPE_LABELS[locationType] || locationType;
                
                let locationDetail = '';
                if (locationType === 'AIRPORT' && stock.current_airport_iata) {
                    locationDetail = stock.current_airport_iata;
                } else if (locationType === 'FLIGHT' && stock.transport_identifier) {
                    locationDetail = stock.transport_identifier;
                } else if (stock.current_other_location) {
                    locationDetail = stock.current_other_location;
                }
                
                return (
                    <Space direction="vertical" size={0}>
                        <Tag color="geekblue">{locationLabel}</Tag>
                        {locationDetail && (
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                {locationDetail}
                            </Text>
                        )}
                    </Space>
                );
            },
        },
        {
            title: '最后动态时间',
            dataIndex: ['stock', 'last_moved_at'],
            key: 'last_moved_at',
            width: 150,
            render: (date) => {
                if (!date) {
                    return <Text type="secondary">从未记录</Text>;
                }
                return (
                    <Space direction="vertical" size={0}>
                        <Text>{dayjs(date).format('YYYY-MM-DD')}</Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            {dayjs(date).format('HH:mm:ss')}
                        </Text>
                    </Space>
                );
            },
        },
        {
            title: '无动态天数',
            dataIndex: 'daysSinceLastMove',
            key: 'daysSinceLastMove',
            width: 120,
            sorter: true,
            render: (days) => {
                if (days === null) {
                    return <Text type="secondary">未知</Text>;
                }
                
                let color = '#666666';
                if (days >= 90) color = '#ff4d4f';
                else if (days >= 60) color = '#fa8c16';
                else if (days >= 30) color = '#fadb14';
                
                return (
                    <Text strong style={{ color, fontSize: '16px' }}>
                        {days} 天
                    </Text>
                );
            },
        },
        {
            title: '风险等级',
            dataIndex: 'riskLevel',
            key: 'riskLevel',
            width: 120,
            filters: [
                { text: '高风险', value: 'high' },
                { text: '中风险', value: 'medium' },
                { text: '低风险', value: 'low' },
                { text: '正常', value: 'normal' },
                { text: '未知', value: 'unknown' },
            ],
            render: (riskLevel, record) => getRiskTag(riskLevel, record.daysSinceLastMove),
        },
        {
            title: (
                <Space>
                    <TagsOutlined />
                    标签
                </Space>
            ),
            dataIndex: ['master', 'tags'],
            key: 'tags',
            width: 200,
            render: (tags) => {
                if (!tags || tags.length === 0) {
                    return <Text type="secondary">无标签</Text>;
                }
                
                return (
                    <Space wrap>
                        {tags.map((tag, index) => (
                            <Tag 
                                key={index} 
                                color={tag.color_code || 'default'}
                                size="small"
                            >
                                {tag.tag_name}
                            </Tag>
                        ))}
                    </Space>
                );
            },
        },
        {
            title: '操作',
            key: 'action',
            width: 100,
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Tooltip title="查看详情">
                        <Button 
                            type="link" 
                            size="small" 
                            icon={<EyeOutlined />}
                            onClick={() => handleViewDetail(record)}
                        >
                            详情
                        </Button>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    // 查看详情
    const handleViewDetail = (record) => {
        const coreStatus = record.stock?.core_status || 'IN_STOCK';
        const availability = record.stock?.availability || 'AVAILABLE';
        const coreStatusName = ULD_CORE_STATUS_LABELS[coreStatus] || coreStatus;
        const availabilityName = ULD_AVAILABILITY_LABELS[availability] || availability;
        
        Modal.info({
            title: `ULD详情 - ${record.master.uld_identifier}`,
            width: 600,
            content: (
                <div>
                    <p><strong>ULD类型:</strong> {record.master.uld_type}</p>
                    <p><strong>所有者:</strong> {record.master.owner}</p>
                    <p><strong>核心状态:</strong> {coreStatusName}</p>
                    <p><strong>可用性:</strong> {availabilityName}</p>
                    <p><strong>最后动态:</strong> {record.stock?.last_moved_at ? dayjs(record.stock.last_moved_at).format('YYYY-MM-DD HH:mm:ss') : '从未记录'}</p>
                    <p><strong>无动态天数:</strong> {record.daysSinceLastMove || '未知'} 天</p>
                    <p><strong>风险等级:</strong> {getRiskTag(record.riskLevel, record.daysSinceLastMove)}</p>
                    {record.master?.tags && record.master.tags.length > 0 && (
                        <div>
                            <strong>标签:</strong>{' '}
                            <Space wrap style={{ marginTop: 4 }}>
                                {record.master.tags.map((tag, index) => (
                                    <Tag 
                                        key={index} 
                                        color={tag.color_code || 'default'}
                                        size="small"
                                    >
                                        {tag.tag_name}
                                    </Tag>
                                ))}
                            </Space>
                        </div>
                    )}
                </div>
            ),
        });
    };

    const handleTableChange = (newPagination, filters, sorter) => {
        setTableFilters(filters || {});
        setTableSorter(sorter || null);
        setPagination((prev) => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        }));
    };

    const handleRefresh = async () => {
        await fetchInactiveUlds();
        message.success('数据已刷新');
    };

    const handleInactiveDaysChange = (newDays) => {
        setInactiveDays(newDays);
        if (onFiltersChange) {
            onFiltersChange({ inactive_days: newDays });
        }
        setPagination((prev) => ({ ...prev, current: 1 }));
    };



    if (error) {
        return (
            <Alert 
                message="数据加载失败" 
                description={error} 
                type="error" 
                showIcon 
            />
        );
    }

    return (
        <div>
            {/* 筛选器 */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={12}>
                    <Select
                        placeholder="无动态天数"
                        value={inactiveDays}
                        onChange={handleInactiveDaysChange}
                        style={{ width: '100%' }}
                    >
                        <Option value={1}>1天以外</Option>
                        <Option value={7}>7天以外</Option>
                        <Option value={15}>15天以外</Option>
                        <Option value={30}>30天以外</Option>
                        <Option value={60}>60天以外</Option>
                        <Option value={90}>90天以外</Option>
                        <Option value={180}>180天以外</Option>
                    </Select>
                </Col>
                <Col span={12}>
                    <Button 
                        icon={<ReloadOutlined />} 
                        onClick={handleRefresh}
                        loading={loading}
                        style={{ width: '100%' }}
                    >
                        刷新
                    </Button>
                </Col>
            </Row>

            {backendTotal > 500 && (
                <Alert
                    type="warning"
                    showIcon
                    message={`列表最多展示 500 条，当前符合条件 ${backendTotal} 条。导出为全量名单。`}
                    style={{ marginBottom: 12 }}
                />
            )}
            <Alert
                type="info"
                showIcon
                message="闲置表示「该去找」；已经确认丢失的板不在此表（看顶栏丢失数或台账筛选丢失）。"
                style={{ marginBottom: 12 }}
            />
            <Table
                columns={columns}
                dataSource={data}
                loading={loading}
                rowKey={(record) => record.master.id}
                size="small"
                bordered
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => 
                        `第 ${range[0]}-${range[1]} 条 / 共 ${total} 条`,
                }}
                scroll={{ x: 1200 }}
                onChange={handleTableChange}
                rowClassName={(record) => {
                    if (record.riskLevel === 'high') return 'high-risk-row';
                    if (record.riskLevel === 'medium') return 'medium-risk-row';
                    return '';
                }}
            />

            <style>{`
                .high-risk-row {
                    background-color: #fff2f0 !important;
                }
                .high-risk-row:hover {
                    background-color: #ffede8 !important;
                }
                .medium-risk-row {
                    background-color: #fff7e6 !important;
                }
                .medium-risk-row:hover {
                    background-color: #ffefd3 !important;
                }
            `}</style>
        </div>
    );
};

export default UldInactiveTable;